import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ApiError,
  appLink,
  type AndroidBuild,
  type AppInstance,
  type PairingCodeResponse,
} from '../lib/api.js';

/**
 * «Приложение» screen — mints one-time pairing codes for our own mobile
 * client and lists the installations already bound to the user.
 *
 * The code is shown exactly once: the API stores only its hash, so a reload
 * cannot bring it back — the user mints a new one instead.
 */
export function AppLinkPage() {
  const [instances, setInstances] = useState<AppInstance[] | null>(null);
  const [code, setCode] = useState<PairingCodeResponse | null>(null);
  const [android, setAndroid] = useState<AndroidBuild>({ available: false });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await appLink.listInstances();
      setInstances(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить список установок.');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    // Раздача APK — необязательная возможность: если статус не тянется,
    // просто не показываем секцию, а не ломаем страницу привязки.
    appLink
      .androidBuild()
      .then(setAndroid)
      .catch(() => setAndroid({ available: false }));
  }, []);

  const onCreateCode = async () => {
    setBusy(true);
    setError(null);
    try {
      setCode(await appLink.createPairingCode());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать код.');
    } finally {
      setBusy(false);
    }
  };

  const onDownloadAndroid = async () => {
    setBusy(true);
    setError(null);
    try {
      const { ticket } = await appLink.androidTicket();
      window.location.href = appLink.androidUrl(ticket);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось получить ссылку на скачивание.');
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (id: string) => {
    if (!confirm('Отвязать установку? Приложение перестанет получать обновления профилей.')) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await appLink.revokeInstance(id);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось отвязать установку.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <div className="mb-6">
        <Link to="/" className="text-sm text-slate-400 hover:text-white">
          ← К списку конфигов
        </Link>
      </div>

      <h1 className="text-2xl font-semibold text-slate-100">Приложение</h1>

      {error && (
        <div className="mt-6 rounded-lg border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {android.available && (
        <section className="mt-6 rounded-xl border border-slate-800 bg-slate-900/50 p-5">
          <h2 className="text-sm font-semibold text-slate-200">Приложение для Android</h2>
          <p className="mt-1 text-xs text-slate-500">
            Устанавливается не из Play Маркета, поэтому Android спросит разрешение на установку из
            этого источника.
          </p>
          <button
            onClick={() => void onDownloadAndroid()}
            disabled={busy}
            className="mt-4 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white shadow hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
          >
            Скачать APK
          </button>
          <div className="mt-2 text-xs text-slate-500">
            {(android.size / 1024 / 1024).toFixed(0)} МБ · обновлено{' '}
            {new Date(android.updatedAt).toLocaleDateString('ru-RU')}
          </div>
        </section>
      )}

      <section className="mt-6 rounded-xl border border-slate-800 bg-slate-900/50 p-5">
        <h2 className="text-sm font-semibold text-slate-200">Подключить установку</h2>
        <p className="mt-1 text-xs text-slate-500">
          Создайте код и откройте его в приложении кнопкой ниже — либо введите вручную, если
          открываете кабинет не с того устройства. Код действует 10 минут и срабатывает один раз.
        </p>
        <button
          onClick={() => void onCreateCode()}
          disabled={busy}
          className="mt-4 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white shadow hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
        >
          Создать код
        </button>
        {code && (
          <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950/60 px-4 py-3">
            <div className="font-mono text-xl tracking-widest text-slate-100">{code.code}</div>
            <div className="mt-1 text-xs text-slate-500">
              Действует до {new Date(code.expiresAt).toLocaleTimeString('ru-RU')}
            </div>
            {/* Схема `nevodapp` зарегистрирована и в iOS, и в Android; если
                приложения на устройстве нет, ссылка молча ничего не сделает —
                поэтому код остаётся на экране, а не прячется за кнопку. */}
            <a
              href={`nevodapp://pair?code=${encodeURIComponent(code.code)}`}
              className="mt-3 inline-block rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white shadow hover:bg-brand-dark"
            >
              Авторизоваться в приложении
            </a>
          </div>
        )}
      </section>

      <section className="mt-8">
        <h2 className="text-lg font-semibold text-slate-200">Подключённые установки</h2>
        {instances === null && <div className="mt-3 text-sm text-slate-500">Загружаем…</div>}
        {instances !== null && instances.length === 0 && (
          <div className="mt-3 rounded-lg border border-dashed border-slate-800 px-4 py-8 text-center text-sm text-slate-500">
            Пока ни одной. Создайте код выше и введите его в приложении.
          </div>
        )}
        {instances !== null && instances.length > 0 && (
          <ul className="mt-3 divide-y divide-slate-800 rounded-xl border border-slate-800">
            {instances.map((instance) => (
              <li key={instance.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm text-slate-100">{instance.label}</div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {instance.platform} · последний вход{' '}
                    {new Date(instance.lastSeenAt).toLocaleString('ru-RU')}
                    {instance.lastIp ? ` · ${instance.lastIp}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => void onRevoke(instance.id)}
                  disabled={busy}
                  className="shrink-0 rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-red-400 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Отвязать
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-slate-500">
          Отвязка прекращает обновления и стирает данные в приложении при следующем обращении. Это
          не отключает сам VPN — для этого отзовите конфиг на странице конфигов.
        </p>
      </section>
    </div>
  );
}
